import type { AllFilterType } from '@carrefour-gcs/shared';

import type {
  ActivityTypesType,
  AssortmentTypologiesType,
  BusinessBrandsType,
  IntegratedFranchisedType,
  ListFiltersType,
  ProductNomenclatureFilterType,
  ProductTypologiesType,
  StoresFilterType
} from './filter.query';

import { type CheckedStateType, type TreeItemsStateType, type TreeViewItemType } from '#components/treeView';

export type FilterFromSearch =
  | ProductNomenclatureFilterType
  | StoresFilterType
  | ActivityTypesType
  | ListFiltersType
  | IntegratedFranchisedType
  | AssortmentTypologiesType
  | ProductTypologiesType
  | BusinessBrandsType;

export function filterToTreeViewItem(
  filter: FilterFromSearch & { disabled?: boolean; disabledMessage?: string },
  selectedFilters: TreeItemsStateType,
  parentState?: CheckedStateType
): TreeViewItemType {
  let state = selectedFilters?.[filter.nodeId]?.state ?? 'unchecked';
  if (state === 'unchecked' && (parentState === 'checked' || parentState === 'checked-by-parent')) {
    state = 'checked-by-parent';
  }

  const children = filter.children?.map((filters) => filterToTreeViewItem(filters, selectedFilters, state)) ?? [];

  const hasOneChildChecked = children.some(
    (child) => child.checkedStatus === 'checked' || child.checkedStatus === 'indeterminate'
  );

  if (state === 'unchecked' && hasOneChildChecked) {
    state = 'indeterminate';
  }

  return {
    filterKey: filter.filterKey,
    label: filter.label,
    type: filter.type as AllFilterType,
    checkedStatus: state,
    children,
    nodeId: filter.nodeId,
    disabled: filter.disabled,
    disabledMessage: filter.disabledMessage
  };
}

interface FilterToTreeViewItemWithDefaultValueArgs {
  filter: FilterFromSearch & { disabled?: boolean; disabledMessage?: string };
  defaultFilters: FilterFromSearch;
  selectedFilters: TreeItemsStateType;
  parentState?: CheckedStateType;
}

export function filterToTreeViewItemWithDefaultValue({
  filter,
  defaultFilters,
  selectedFilters,
  parentState
}: FilterToTreeViewItemWithDefaultValueArgs): TreeViewItemType {
  let state = selectedFilters[defaultFilters.nodeId]?.state ?? 'unchecked';
  if (state === 'unchecked' && (parentState === 'checked' || parentState === 'checked-by-parent')) {
    state = 'checked-by-parent';
  }

  const children =
    defaultFilters.children?.map((filters) => filterToTreeViewItem(filters, selectedFilters, state)) ?? [];

  const hasOneChildChecked = children.some(
    (child) => child.checkedStatus === 'checked' || child.checkedStatus === 'indeterminate'
  );

  if (state === 'unchecked' && hasOneChildChecked) {
    state = 'indeterminate';
  }

  if (filter.filterKey === 'root') {
    const rootChildren =
      children?.filter((child) => {
        const existOnFilter = filter.children?.some(
          (filterChild: FilterFromSearch) => filterChild.nodeId === child.nodeId
        );

        const isChecked = child.checkedStatus !== 'unchecked';

        return existOnFilter || isChecked;
      }) ?? [];

    return {
      filterKey: filter.filterKey,
      label: filter.label,
      type: filter.type as AllFilterType,
      checkedStatus: state,
      children: rootChildren,
      nodeId: filter.nodeId,
      disabled: filter.disabled,
      disabledMessage: filter.disabledMessage
    };
  }

  return {
    filterKey: filter.filterKey,
    label: filter.label,
    type: filter.type as AllFilterType,
    checkedStatus: state,
    children,
    nodeId: filter.nodeId,
    disabled: filter.disabled,
    disabledMessage: filter.disabledMessage
  };
}
